/* eslint-disable */ // too many errors to hit now
import _values from 'lodash/values';
import Queue from '../util/tinyqueue';

/** Converts a polygon (an array of vectors) to an array adjacent points (an array of arrays of vectors) */
export function segments(poly) {
  const len = poly.length - 1,
    result = new Array(poly.length);
  for (let i = 0; i < len; i++) {
    result[i] = [poly[i], poly[i + 1]];
  }

  result[len] = [poly[len], poly[0]];
  return result;
}

/** Clone a vector -or- get the zero vector */
export const vec = ({ x, y } = { x: 0, y: 0 }) => ({ x, y });

/** Computes the midpoint of two or more points. */
export function mid(poly) {
  const len = poly.length;
  if (len === 0) return;

  const sum = vec();
  for (let i = 0; i < len; i++) vadd(sum, poly[i]);
  return div(sum, len);
}

/** Add two points (vectors). NOTE: This method modifies the first point. */
export function vadd(a, b) {
  a.x += b.x;
  a.y += b.y;
  return a;
}

/** Subtract two points (vectors). NOTE: This method modifies the first point. */
export function vsub(a, b) {
  a.x -= b.x;
  a.y -= b.y;
  return a;
}

/** Add a vector to a polygon (an array of vectors). NOTE: This method modifies the original polygon. */
export function add(poly, { x, y }) {
  for (let i = 0, len = poly.length; i < len; i++) {
    poly[i].x += x;
    poly[i].y += y;
  }
  return poly;
}

/** Subtract a vector from a polygon (an array of vectors). NOTE: This method modifies the original polygon. */
export function sub(poly, { x, y }) {
  for (let i = 0, len = poly.length; i < len; i++) {
    poly[i].x -= x;
    poly[i].y -= y;
  }
  return poly;
}

/** Multiply a vector by a scalar. NOTE: This method modifies the original point. */
export function scale(a, m) {
  a.x *= m;
  a.y *= m;
  return a;
}

/** Divide a vector by a scalar. NOTE: This method modifies the original point. */
export function div(a, d) {
  if (d === 0) throw new Error('Cannot divide by zero');
  a.x /= d;
  a.y /= d;
  return a;
}

/** Get the norm / magnitude of a vector (returns a scalar) */
export const mag = ({ x, y }) => Math.hypot(x, y);

/** Get the distance between two points, i.e. the magnitude of their vector difference. */
export const dist = ({ x: x1, y: y1 }, { x: x2, y: y2 }) => Math.hypot(x2 - x1, y2 - y1);

/** Get a unit vector, optionally multiplied by a scalar. NOTE: This method modifies the original vector. */
export const unit = (v, m = 1) => div(v, mag(v) / m);

/** Compute the perpendicular of a vector. */
export const perp = ({ x, y }) => ({ x: -y, y: x });

/** Compute the dot product (scalar product) of two vectors. */
export const dot = ({ x: x1, y: y1 }, { x: x2, y: y2 }) => x1 * x2 + y1 * y2;

/** Compute the cross product (scalar product) of two vectors. */
export const cross = ({ x: x1, y: y1 }, { x: x2, y: y2 }) => x1 * y2 - y1 * x2;

/** Rotate a polygon (array of vectors) a given number of radians, optionally around a given orgin.
 *  NOTE: This method modifies the original polygon. */
export function rot(poly, rad, origin = { x: 0, y: 0 }) {
  const cos = Math.cos(rad),
    sin = Math.sin(rad);
  for (let i = 0, len = poly.length; i < len; i++) {
    const dx = poly[i].x - origin.x,
      dy = poly[i].y - origin.y;
    poly[i].x = origin.x + (dx * cos - dy * sin);
    poly[i].y = origin.y + (dx * sin + dy * cos);
  }

  return poly;
}

/** Compute the area of a polygon (an array of vectors). NOTE: Won't work for self-intersecting polgons. */
export function area(poly) {
  const area = segments(poly).reduce((acc, [a, b]) => acc + (a.x * b.y - a.y * b.x), 0);
  return Math.abs(area / 2);
}

/** Compute the bounding box of a polygon. */
export function boundingBox(poly) {
  const len = poly.length;
  if (len === 0) return;

  let p = poly[0];
  let minX = p.x,
    minY = p.y,
    maxX = p.x,
    maxY = p.y;
  for (let i = 0; i < len; i++, p = poly[i]) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return {
    minX, minY, maxX, maxY,
  };
}

/** Computes the centroid of two or more points. */
export function centroid(poly) {
  const len = poly.length;
  if (len === 0) return;

  const sum = vec();
  const area = segments(poly).reduce((acc, [p, q]) => {
    const f = p.x * q.y - p.y * q.x;
    vadd(sum, scale(vadd(vec(p), q), f));
    return acc + f;
  }, 0);
  return div(sum, 3 * area);
}

// various tools for calculating the label position.
function _distToPoly(x, y, poly) {
  let sign = -1;
  let min = Infinity;
  for (let i = 0, len = poly.length, j = len - 1; i < len; j = i++) {
    const a = poly[i];
    const b = poly[j];

    if (((a.y > y) !== (b.y > y)) && (x < (b.x - a.x) * (y - a.y) / (b.y - a.y) + a.x)) sign *= -1;

    min = Math.min(min, _distToSeg2(x, y, a, b));
  }

  return sign * Math.sqrt(min);
}
function _distToSeg2(px, py, a, b, horizontalness = 3) {
  let { x, y } = a;
  let dx = b.x - x;
  let dy = b.y - y;

  if (dx !== 0 || dy !== 0) {
    const t = ((px - x) * dx + (py - y) * dy) / (dx * dx + dy * dy);

    if (t > 1) {
      x = b.x;
      y = b.y;
    } else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }

  dx = px - x;
  dy = py - y;

  return dx * dx + dy * dy * horizontalness;
}
const _compareMax = (a, b) => b.max - a.max;
const _cell = (x, y, h, poly) => {
  const d = _distToPoly(x, y, poly);
  return ({
    x, y, h, d, max: d + h * Math.SQRT2,
  });
};
export function getLabelPosition(poly, precision = 1.0) {
  // find the bounding box of the outer ring
  const {
    minX, minY, maxX, maxY,
  } = boundingBox(poly);
  const width = maxX - minX;
  const height = maxY - minY;
  const cellSize = Math.min(width, height);
  let h = cellSize / 2;

  if (cellSize === 0) return { x: minX, y: minY };

  // a priority queue of cells in order of their "potential" (max distance to polygon)
  const cellQueue = new Queue([], _compareMax);

  // cover polygon with initial cells
  for (let x = minX; x < maxX; x += cellSize) {
    for (let y = minY; y < maxY; y += cellSize) {
      cellQueue.push(_cell(x + h, y + h, h, poly));
    }
  }

  // take centroid as the first best guess
  const cntr = centroid(poly);
  let bestCell = _cell(cntr.x, cntr.y, 0, poly);

  // special case for rectangular polygons
  const bboxCell = _cell(minX + width / 2, minY + height / 2, 0, poly);
  if (bboxCell.d > bestCell.d) bestCell = bboxCell;

  while (cellQueue.length && cellQueue.length < 100) {
    // pick the most promising cell from the queue
    const cell = cellQueue.pop();

    // update the best cell if we found a better one
    if (cell.d > bestCell.d) {
      bestCell = cell;
    }

    // do not drill down further if there's no chance of a better solution
    if (cell.max - bestCell.d <= precision) continue;

    // split the cell into four cells
    h = cell.h / 2;
    cellQueue.push(_cell(cell.x - h, cell.y - h, h, poly));
    cellQueue.push(_cell(cell.x + h, cell.y - h, h, poly));
    cellQueue.push(_cell(cell.x - h, cell.y + h, h, poly));
    cellQueue.push(_cell(cell.x + h, cell.y + h, h, poly));
  }

  return { x: bestCell.x, y: bestCell.y };
}


/** Compute the area of backsplash(es) associated with the edges of a shape */
export function backsplashArea({ edges }) {
  return _values(edges).reduce((acc, e) => acc + (e.hasBacksplash ? e.backsplashHeight * e.length : 0), 0);
}

const _isAgainstWall = (edges, edgeMeta) => edgeMeta && edges[edgeMeta.key] && edges[edgeMeta.key].isAgainstWall;
/** Compute the area of backsplash(es) associated with the edges of a shape */
export function finishedEdgeLength({ edges }, poly, meta) {
  const len = poly.length;
  let sum = 0;
  for (let i = 0; i < len; i++) {
    if (_isAgainstWall(edges, meta.edges[i])) continue;

    sum += dist(poly[i], poly[(i + 1) % len]);
  }
  return sum;
}

/** Compute the number of finished interior and exterior corners of a shape */
export function finishedCornerCount({ edges }, poly, meta) {
  const len = poly.length;
  let interior = 0,
    exterior = 0;
  for (let i = 0; i < len; i++) {
    if (_isAgainstWall(edges, meta.edges[i]) ||
        _isAgainstWall(edges, meta.edges[(i + 1) % len])) continue;

    const p = poly[i],
      q = poly[(i + 1) % len],
      r = poly[(i + 2) % len];
    const x1 = p.x - q.x,
      y1 = p.y - q.y,
      x2 = r.x - q.x,
      y2 = r.y - q.y;
    if ((x1 * y2 - y1 * x2) > 0) {
      exterior++;
    } else {
      interior++;
    }
  }
  return {
    interior,
    exterior,
  };
}
